import * as React from 'react';
import { LEGACY_METADATA_VERSION, normalizeApiVersion } from '../../services/ApiVersionService';

export const WorkflowEditorContext = React.createContext(null);

export const WorkflowEditorProvider = ({ value, children }) => (
  <WorkflowEditorContext.Provider value={value}>
    {children}
  </WorkflowEditorContext.Provider>
);

export const useWorkflowEditor = () => React.useContext(WorkflowEditorContext);

/**
 * Scopes the editor context to the node (or custom setting / card) being edited: the same
 * editor value plus `node.metadataVersion`, so pickers and token lists nested anywhere in
 * the node editor read the entity/field metadata of THAT node's version without every
 * editor threading a prop through. The graph has no version; nodes do.
 */
export const WorkflowNodeScopeProvider = ({ metadataVersion, workflowDefinitionID, nodeId, children }) => {
  const editor = useWorkflowEditor();
  const value = React.useMemo(() => ({
    ...(editor ?? {}),
    node: {
      ...(editor?.node ?? {}),
      metadataVersion: normalizeApiVersion(metadataVersion, LEGACY_METADATA_VERSION),
      // The node's identity, for anything nested that keys state by node (the AI
      // assistant's server-kept sessions): the workflow's id and the node's id.
      workflowDefinitionID: workflowDefinitionID ?? editor?.node?.workflowDefinitionID ?? null,
      nodeId: (nodeId ?? editor?.node?.nodeId ?? '').toString(),
    },
  }), [editor, metadataVersion, workflowDefinitionID, nodeId]);
  return (
    <WorkflowEditorContext.Provider value={value}>
      {children}
    </WorkflowEditorContext.Provider>
  );
};

/**
 * Effective metadata version for the node being edited. Outside a node scope (or for a
 * node authored before versioning) this is the legacy v2 metadata.
 */
export const useNodeMetadataVersion = () => {
  const editor = useWorkflowEditor();
  return normalizeApiVersion(editor?.node?.metadataVersion, LEGACY_METADATA_VERSION);
};

/**
 * The AI assistant session host for the node being edited: its server-kept sessions
 * group by workflow + node (+ `part`, when one node hosts more than one assistant, e.g.
 * a template editor's several queries). Undefined outside a node scope, so the dialog
 * falls back to its in-memory session.
 */
export const useWorkflowNodeSessionHost = (part) => {
  const editor = useWorkflowEditor();
  const workflowDefinitionID = editor?.node?.workflowDefinitionID ?? null;
  const nodeId = (editor?.node?.nodeId ?? '').toString().trim();
  const partKey = (part ?? '').toString().trim();
  return React.useMemo(() => {
    if (!nodeId && !workflowDefinitionID) return undefined;
    return {
      hostEntity: 'WorkflowDefinition',
      hostEntityID: workflowDefinitionID,
      hostSubKey: [nodeId, partKey].filter(Boolean).join(':'),
    };
  }, [workflowDefinitionID, nodeId, partKey]);
};
